import { createSharedSelector, createSharedState } from 'preact-shared-state-hook'
import type { DoodlePermissions, Folder } from '../../../server/src/models'
import { URL_API_FOLDER, URL_API_FOLDERS, URL_API_FOLDER_SHARED_USER, URL_API_FOLDER_SHARED_USERS } from '../../../server/src/urls'
import { i18n } from '../../shared/i18n'
import { notifyError } from '../../shared/notifyError'
import { comparing, mapValues, namesAndEmails, patchJson, postJson, withDefaultErrorHandler } from '../../shared/utils'
import { getCurrentUser, loadCurrentUser } from './account'

const [useFolders, setFolders] = createSharedState<readonly Folder[]>([])
const updateFolder = (folderId: string, update: (folder: Folder) => Folder) =>
  setFolders(folders => folders.map(folder => (folder.id === folderId ? update(folder) : folder)))

export { useFolders }

export const usePersonalFolders = createSharedSelector([useFolders], folders =>
  folders.filter(folder => !folder.tenantSharing && !folder.shared).sort(comparing(({ name }: { name: string }) => name)),
)
export const useSharedFolders = createSharedSelector([useFolders], folders =>
  folders.filter(folder => !folder.tenantSharing && folder.shared).sort(comparing(({ name }: { name: string }) => name)),
)
export const useTenantFolders = createSharedSelector([useFolders], folders =>
  folders.filter(folder => folder.tenantSharing).sort(comparing(({ name }: { name: string }) => name)),
)

export const loadFolders = async () => {
  const response = await fetch(URL_API_FOLDERS)
  const folders = response.ok && ((await response.json()) as Folder[])
  Array.isArray(folders) && setFolders(folders)
  return response
}

export const loadGuestFolder = async (folderId: string) => {
  const response = await fetch(URL_API_FOLDER(folderId))
  if (response.ok) {
    const folder = (await response.json()) as Folder
    setFolders([folder])
  }
  return response
}

export const getFolder = (folderId: string) => (folderId ? useFolders.snapshot().find(folder => folder.id === folderId) : undefined)

export const createFolder = async (options?: { shared?: 1; tenantSharing?: 1 }) => {
  const name = prompt(i18n.folderName)
  if (!name) {
    return
  }
  const response = await withDefaultErrorHandler(
    postJson(URL_API_FOLDERS, { name, parentFolderId: '', shared: options?.shared, tenantSharing: options?.tenantSharing }),
    notifyError,
  )
  if (!response) {
    return
  }
  try {
    const folder = (await response.json()) as Folder
    setFolders(folders => [...folders, folder])
  } catch {
    alert(i18n.errorOccurred)
    location.reload()
  }
}

export const renameFolder = async (folderId: string, currentName: string) => {
  const name = prompt(i18n.folderName, currentName)
  if (name && (await withDefaultErrorHandler(patchJson(URL_API_FOLDER(folderId), { name })), notifyError)) {
    setFolders(folders => folders.map(folder => (folder.id === folderId ? { ...folder, name } : folder)))
  }
}

export const deleteFolder = async (folderId: string) => {
  if (!confirm(i18n.confirmDelete)) {
    return
  }
  if (await withDefaultErrorHandler(fetch(URL_API_FOLDER(folderId), { method: 'DELETE' }), notifyError)) {
    setFolders(folders => folders.filter(folder => folder.id !== folderId))
  }
}

export const setFolderOwner = async (folder: Folder, ownerUserId: string) => {
  if (await withDefaultErrorHandler(patchJson(URL_API_FOLDER(folder.id), { ownerUserId }))) {
    setFolders(folders => folders.filter(({ id }) => id !== folder.id))
    // ストレージ使用量の調整が大変なので取り直します。
    await withDefaultErrorHandler(loadCurrentUser())
  }
}

export const inviteToFolder = async (
  folder: Folder,
  users: readonly { readonly email: string; readonly name?: string }[],
  permissions?: DoodlePermissions,
) => {
  const currentUser = getCurrentUser()
  if (!currentUser || currentUser.guest || currentUser.uid !== folder.ownerUserId) {
    return alert(i18n.errorOccurred)
  }
  const sharedUsers = Object.values(folder.users).filter(u => u.uid !== currentUser.uid && !u.deleted)
  const sharedUsersCount = sharedUsers.filter(u => !u.deleted).length
  const tenantId = currentUser?.primary_tenant_id
  const maxGuestCount = tenantId ? currentUser?.tenants?.[tenantId]?.zumeco?.license?.maxFolderGuestCount ?? 0 : 0
  if (sharedUsersCount + users.length > maxGuestCount) {
    return alert(i18n.sharing.maxSharingUserCountExceeded)
  }
  if (users.some(({ email }) => email === currentUser.email)) {
    return alert(i18n.sharing.cantInviteYourself)
  }
  {
    const invitedYet = users.filter(({ email }) => sharedUsers.some(user => user.email === email))
    if (invitedYet.length !== 0) {
      return alert(i18n.sharing.invitedYet(namesAndEmails(invitedYet)))
    }
  }
  const response = await withDefaultErrorHandler(postJson(URL_API_FOLDER_SHARED_USERS(folder.id), { users, permissions }))
  if (response) {
    const newUsers = (await response.json()) as Folder['users']
    updateFolder(folder.id, folder => ({ ...folder, users: { ...folder.users, ...newUsers } }))
    alert(i18n.sharing.sentInvitationMail)
    return true
  }
}

export const unshareFolder = async (folder: Folder, user: { readonly uid: string }) => {
  if (confirm(i18n.sharing.confirmUnsharing)) {
    await withDefaultErrorHandler(fetch(URL_API_FOLDER_SHARED_USER(folder.id, user.uid), { method: 'DELETE' }))
    updateFolder(folder.id, folder => ({
      ...folder,
      users: Object.fromEntries(Object.entries(folder.users).filter(([uid]) => uid !== user.uid)),
    }))
  }
}

export const updateFolderUserPermissions = async (folder: Folder, user: { readonly uid: string }, permissions: DoodlePermissions) => {
  await withDefaultErrorHandler(patchJson(URL_API_FOLDER_SHARED_USER(folder.id, user.uid), { permissions }))
  updateFolder(folder.id, folder => ({
    ...folder,
    users: mapValues(folder.users, u => (u.uid !== user.uid ? u : { ...u, permissions: { ...u.permissions, ...permissions } })),
  }))
}
